import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { compareAsc, subMinutes } from "date-fns";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import api from "../services/api";
import { decrypt } from "../utils/crypto";
import { notifyError } from "../utils/notifications";
import inactivityTime from "./useInactivityTime";
import { storageService } from "../services/storageService";

type TAuthContext = {
  user?: {
    company_id: number;
    cpf: string | null;
    created_at: string;
    created_by: number | null;
    deleted_at: string | null;
    email: string;
    has_beta_active: boolean;
    id: number;
    key: number;
    login: string;
    is_sso_active: boolean;
    password_expired: boolean;
    qqq: unknown;
    role_id: number;
    role_name: string;
    status: "Ativo" | "Inativo" | string;
    companyhasCpfRequired: boolean;
    store: unknown;
    storeIds: number[];
    storeNames: string[];
    type_user: string;
    typeUser: string;
    type_role_user?: string;
    updated_at: string;
    username: string;
    domain_registered: boolean;
    domain_active: boolean;
    identity?: string;
  };
  signIn: (data: { login: string; password: string }) => Promise<void>;
  signOut: (login: string) => Promise<void>;
  updateUser: (user: TAuthContext["user"]) => void;
  clearLocalStorage: () => void;
  signInSSO: (kcToken: string) => Promise<void | false>;
  fetchMyUser: () => Promise<void>;
};

const baseUrl = process.env.REACT_APP_HOST_BACKEND;
const AuthContext = createContext<TAuthContext>({} as TAuthContext);

interface AuthProviderInterface {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderInterface) {
  const [data, setData] = useState(() => {
    const token = storageService.getToken();

    if (token) {
      return {
        token,
        user: undefined,
      };
    }
    return {};
  });

  const clearCacheData = () => {
    caches.keys().then((names) => {
      names.forEach((name) => {
        caches.delete(name);
      });
    });
  };

  const clearLocalStorage = useCallback(() => {
    storageService.clearAll();
    clearCacheData();
    setData({});
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      // MUDANÇA: Verificação de expiração agora usa o token do service
      const token = storageService.getToken();
      if (token) {
        const jwt = token.split(".");
        if (jwt.length === 3) {
          const jwtPayload = jwt[1];
          const payload = JSON.parse(atob(jwtPayload));
          const expirationTime = subMinutes(new Date(payload.exp * 1000), 5);
          if (compareAsc(expirationTime, new Date()) === -1) {
            console.info("Refreshing token ...");
            api.refreshToken();
          }
        }
      }
    }, 120000); // 2 minutes

    return () => clearInterval(interval);
  }, []);

  const decryptCPF = useCallback((cpf) => {
    return cpf ? decrypt(cpf) : "";
  }, []);

  const signIn = useCallback(
    async ({ login, password }) => {
      
      const existingToken = storageService.getToken();
      if (existingToken) {
        window.location.href = "/";
        return;
      }
      
      const newSessionId = uuidv4();
      const response = await api.postLogin("login", {
        login,
        password,
        newSessionId,
      });
      const {
        token,
        user,
        processor,
        passwordExpired,
        env,
        isPch,
        sessionId,
        allowAccess,
      } = response;

      if (response.statusCode === 409) {
        notifyError(response.message, "ERROR_HIGHLIGHT", false, true);
        return;
      }

      user.identity = processor.identity;
      user.cpf = decryptCPF(user.cpf);

      clearLocalStorage();

      storageService.setAuthData({
        token: token.token,
        refreshToken: token.refreshToken,
        env,
        identity: processor.identity,
        passwordExpired,
        isPch,
        sessionId,
        allowAccess,
      });

      setData({ token: token.token, user });
    },
    [decryptCPF, clearLocalStorage]
  );

  const signInSSO = async (kcToken: string) => {

    const existingToken = storageService.getToken();
      if (existingToken) {
        window.location.href = "/";
        return;
      }
    
      try {
      const response = await axios.post(
        `${baseUrl}/sso_login`,
        {},
        {
          headers: {
            Authorization: `Bearer ${kcToken}`,
          },
        }
      );

      const {
        token,
        user,
        processor,
        passwordExpired,
        env,
        isPch,
        sessionId,
        allowAccess,
      } = response.data;

      if (response.data.statusCode === 409) {
        notifyError(response.data.message, "ERROR_HIGHLIGHT", false, true);
        throw new Error(response.data.message);
      }

      user.identity = processor.identity;
      user.cpf = decryptCPF(user.cpf);

      clearLocalStorage();

      storageService.setAuthData({
        token,
        refreshToken: "",
        env,
        identity: processor.identity,
        passwordExpired,
        isPch,
        sessionId,
        allowAccess,
      });

      setData({ token, user });
    } catch (error) {
      console.error("Erro ao fazer login SSO", error);
      throw error;
    }
  };

  const signOut = useCallback(
    async (login) => {
      try {
        await api.post("logout", { login });
      } finally {
        clearLocalStorage();
        clearCacheData();
      }
    },
    [clearLocalStorage]
  );

  const updateUser = useCallback(
    (user) => {
      setData((state) => ({
        ...state,
        user: {
          ...user,
          cpf: decryptCPF(user.cpf),
        },
      }));
    },
    [decryptCPF]
  );

  const fetchMyUser = useCallback(async () => {
    try {
      const response = await api.get("get_my_user");
      updateUser(response.user);

      // MUDANÇA: Usa o método específico do service para a flag de CPF
      storageService.setCpfRequired(response.user?.companyhasCpfRequired);
    } catch (error) {
      console.error("Erro ao buscar informações do usuário", error);
    }
  }, [updateUser]);

  useEffect(() => {
    const token = storageService.getToken();
    if (token) {
      fetchMyUser();
    }
  }, [fetchMyUser]);

useEffect(() => {
    const isUserLogged = !!data.user;
    let cleanupInactivity: (() => void) | undefined;

    if (isUserLogged) {
      cleanupInactivity = inactivityTime() as unknown as (() => void);
    }

    return () => {
      if (typeof cleanupInactivity === 'function') {
            cleanupInactivity();
      }
    };
  }, [data.user]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === storageService.KEYS.TOKEN || e.key === storageService.KEYS.SESSION_ID) {
        sessionStorage.clear(); 
        window.location.reload();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const value = useMemo(
    () => ({
      user: data.user,
      signIn,
      signOut,
      updateUser,
      clearLocalStorage,
      signInSSO,
      fetchMyUser,
    }),
    [
      clearLocalStorage,
      data.user,
      signIn,
      signOut,
      updateUser,
      signInSSO,
      fetchMyUser,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
